import { ForgotPasswordRequest, ForgotPasswordResponse } from "../types/auth";

// Read BASE_URL from environment variable (same pattern as api.ts)
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? "" : "https://pabauto.garage360.app");

/**
 * Helper function to get API URL
 */
const getApiUrl = (endpoint: string): string => {
  if (import.meta.env.DEV && !BASE_URL) {
    // In development with no baseUrl, use relative URL to leverage Vite proxy
    return endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  }
  return `${BASE_URL}${endpoint}`;
};

/**
 * User Service for authentication-related API calls
 */
export class UserService {
  /**
   * Request OTP for password reset
   * @param request - ForgotPasswordRequest with EmailId
   * @returns Promise<string> - JSON string response
   */
  static async ForgotPassword(
    request: ForgotPasswordRequest
  ): Promise<string> {
    try {
      const forgotPasswordUrl = getApiUrl(
        `/pabws/users/forgotpassword/${encodeURIComponent(request.EmailId)}`
      );

      const response = await fetch(forgotPasswordUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseContent = await response.text();
      return responseContent; // Returns JSON string
    } catch (error: unknown) {
      console.error("Error in ForgotPassword:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return JSON.stringify({ success: false, message: errorMessage });
    }
  }

  /**
   * Validate OTP code
   * @param request - ForgotPasswordRequest with ValidationCode
   * @returns Promise<string> - JSON string response
   */
  static async ValidateCode(request: ForgotPasswordRequest): Promise<string> {
    try {
      if (!request.ValidationCode) {
        return JSON.stringify({
          success: false,
          message: "Validation code is required",
        });
      }

      const validateCodeUrl = getApiUrl(
        `/pabws/users/vaildate_code/${encodeURIComponent(request.ValidationCode)}`
      );

      const response = await fetch(validateCodeUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseContent = await response.text();
      return responseContent; // Returns JSON string
    } catch (error: unknown) {
      console.error("Error in ValidateCode:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return JSON.stringify({ success: false, message: errorMessage });
    }
  }

  /**
   * Reset password after OTP validation
   * @param request - ForgotPasswordRequest with EmailId and Password
   * @returns Promise<string> - JSON string response
   */
  static async ResetPassword(request: ForgotPasswordRequest): Promise<string> {
    try {
      if (!request.Password || !request.EmailId) {
        return JSON.stringify({
          success: false,
          message: "Password and email are required",
        });
      }

      // URL encode password and email to handle special characters
      const encodedPassword = encodeURIComponent(request.Password);
      const encodedEmail = encodeURIComponent(request.EmailId);

      const resetUrl = getApiUrl(
        `/pabws/users/reset_password/${encodedPassword}/${encodedEmail}`
      );

      const response = await fetch(resetUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseContent = await response.text();
      return responseContent; // Returns JSON string
    } catch (error: unknown) {
      console.error("Error in ResetPassword:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return JSON.stringify({ success: false, message: errorMessage });
    }
  }
}
